import React, { useState, useMemo } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import WorkOrderHistoryLog from "../TabsCard/WorkOrderHistoryLog";
import { Avatar, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getWorkOrderLogsById } from "../../../services/api/workOrder";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatEventLabel } from "../../../utils/workOrderLogEventLabel";

dayjs.extend(relativeTime);

const EVENT_META = {
  WORK_ORDER_CREATED: { icon: "fa-file-circle-plus", color: "text-blue-600", bg: "bg-blue-50" },
  WORK_ORDER_UPDATED: { icon: "fa-file-pen", color: "text-blue-500", bg: "bg-blue-50" },
  WORK_ORDER_NOTE_UPDATED: { icon: "fa-note-sticky", color: "text-blue-400", bg: "bg-blue-50" },
  WORK_ORDER_DELETED: { icon: "fa-file-circle-xmark", color: "text-red-500", bg: "bg-red-50" },
  WORK_ORDER_STATUS_UPDATED: { icon: "fa-arrows-rotate", color: "text-teal-600", bg: "bg-teal-50" },
  STATUS_UPDATED: { icon: "fa-arrows-rotate", color: "text-teal-600", bg: "bg-teal-50" },
  ASSIGNED_ENGINEER: { icon: "fa-user-plus", color: "text-orange-500", bg: "bg-orange-50" },
  UNASSIGNED_ENGINEER: { icon: "fa-user-minus", color: "text-orange-400", bg: "bg-orange-50" },
  OFFER_SENT: { icon: "fa-handshake", color: "text-amber-600", bg: "bg-amber-50" },
  OFFER_UPDATED: { icon: "fa-handshake", color: "text-amber-500", bg: "bg-amber-50" },
  OFFER_ACCEPTED: { icon: "fa-handshake", color: "text-green-600", bg: "bg-green-50" },
  OFFER_REJECTED: { icon: "fa-handshake-slash", color: "text-red-500", bg: "bg-red-50" },
  OFFER_WITHDRAWN: { icon: "fa-handshake-slash", color: "text-red-400", bg: "bg-red-50" },
  OFFER_DECLINED: { icon: "fa-handshake-slash", color: "text-red-400", bg: "bg-red-50" },
  OFFER_ASSIGN: { icon: "fa-user-check", color: "text-green-500", bg: "bg-green-50" },
  OFFER_UNASSIGNED: { icon: "fa-user-xmark", color: "text-gray-500", bg: "bg-gray-50" },
  OFFER_ROUTED: { icon: "fa-route", color: "text-blue-500", bg: "bg-blue-50" },
  PAYMENT_UPDATED: { icon: "fa-money-bill-wave", color: "text-green-600", bg: "bg-green-50" },
  PAYMENT_APPROVED: { icon: "fa-circle-check", color: "text-green-700", bg: "bg-green-50" },
  CUSTOMER_PAYMENT_UPDATED: {
    icon: "fa-money-bill-wave",
    color: "text-green-500",
    bg: "bg-green-50",
  },
  ENGINEER_PAYMENT_UPDATED: {
    icon: "fa-money-bill-wave",
    color: "text-green-500",
    bg: "bg-green-50",
  },
  TIMELOG_ADDED: { icon: "fa-clock", color: "text-purple-600", bg: "bg-purple-50" },
  TIMELOG_UPDATED: { icon: "fa-clock-rotate-left", color: "text-purple-500", bg: "bg-purple-50" },
  CUSTOMER_TIMESHEET_UPDATED: {
    icon: "fa-table-list",
    color: "text-purple-400",
    bg: "bg-purple-50",
  },
  ENGINEER_TIMESHEET_UPDATED: {
    icon: "fa-table-list",
    color: "text-purple-400",
    bg: "bg-purple-50",
  },
  EXPENSE_ADDED: { icon: "fa-receipt", color: "text-rose-500", bg: "bg-rose-50" },
  EXPENSE_REMOVED: { icon: "fa-receipt", color: "text-gray-500", bg: "bg-gray-50" },
  EXPENSE_APPROVED: { icon: "fa-receipt", color: "text-green-600", bg: "bg-green-50" },
  COMMENT_ADDED: { icon: "fa-comment-lines", color: "text-indigo-500", bg: "bg-indigo-50" },
  FILE_UPLOADED: { icon: "fa-paperclip", color: "text-gray-500", bg: "bg-gray-50" },
  DELIVERABLE_UPLOADED: { icon: "fa-file-arrow-up", color: "text-slate-600", bg: "bg-slate-50" },
  DELIVERABLE_UPLOADED_DELETED: {
    icon: "fa-file-circle-minus",
    color: "text-red-400",
    bg: "bg-red-50",
  },
  CUSTOMER_FILE_UPLOADED: { icon: "fa-file-arrow-up", color: "text-slate-500", bg: "bg-slate-50" },
  CUSTOMER_FILE_DELETED: { icon: "fa-file-circle-minus", color: "text-red-400", bg: "bg-red-50" },
  ENGINEER_CHECKED_IN: { icon: "fa-location-dot", color: "text-teal-600", bg: "bg-teal-50" },
  ENGINEER_CHECKED_OUT: { icon: "fa-location-dot", color: "text-gray-500", bg: "bg-gray-50" },
  SIGN_OFF_SHEET_CREATED: { icon: "fa-file-signature", color: "text-green-600", bg: "bg-green-50" },
  SIGN_OFF_SHEET_UPDATED: { icon: "fa-file-signature", color: "text-green-500", bg: "bg-green-50" },
  FEEDBACK_GIVEN_TO_ENGINEER: { icon: "fa-star", color: "text-yellow-500", bg: "bg-yellow-50" },
  FEEDBACK_GIVEN_TO_CLIENT: { icon: "fa-star", color: "text-yellow-500", bg: "bg-yellow-50" },
  WORK_ORDER_CANCELLED_BY_CLIENT: { icon: "fa-ban", color: "text-red-500", bg: "bg-red-50" },
  WORK_ORDER_CANCELLED_BY_ENGINEER: { icon: "fa-ban", color: "text-red-500", bg: "bg-red-50" },
  WORK_ORDER_PO_ADDED: { icon: "fa-file-invoice", color: "text-violet-500", bg: "bg-violet-50" },
  WORK_ORDER_PO_UPDATED: { icon: "fa-file-invoice", color: "text-violet-500", bg: "bg-violet-50" },
  WORK_ORDER_PO_DELETED: { icon: "fa-file-invoice", color: "text-red-400", bg: "bg-red-50" },
  OTHER: { icon: "fa-circle-info", color: "text-gray-400", bg: "bg-gray-50" },
};

const getEventMeta = (logType) => EVENT_META[logType] || EVENT_META.OTHER;

const HistoryTab = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [selectedLogId, setSelectedLogId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const { data: workOrderLogs, isLoading } = useQuery({
    queryKey: ["workOrderLogs", id, paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      getWorkOrderLogsById(id, {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      }),
    enabled: !!id,
  });

  const openLogDrawer = (logId) => {
    setSelectedLogId(logId);
    setDrawerOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        field: "event",
        headerName: "Event",
        flex: 2,
        renderCell: (params) => {
          const { logType, fields } = params.row;
          const meta = getEventMeta(logType);
          const label = formatEventLabel(logType, params.value, fields);
          return (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => openLogDrawer(params.row.id)}
            >
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${meta.bg} shrink-0`}
              >
                <i className={`fa-light ${meta.icon} ${meta.color} text-xs`} />
              </span>
              <p className="table-font-size text-primary font-normal truncate">{label}</p>
            </div>
          );
        },
      },
      {
        field: "performedBy",
        headerName: "By User",
        flex: 2,
        renderCell: (params) => {
          const user = params?.row?.performedBy;
          return (
            <div
              className={`group flex items-center gap-2 ${user?.id ? "cursor-pointer" : ""}`}
              onClick={user?.id ? () => navigate(`/user/details/profile/${user.id}`) : undefined}
            >
              <Avatar
                src={user?.url || "/default-avatar.png"}
                alt={user?.name}
                sx={{ width: 30, height: 30 }}
              />
              <div className="flex flex-col">
                <p
                  className={`text-sm font-normal text-gray-800 leading-tight ${
                    user?.id ? "group-hover:text-primary group-hover:underline" : ""
                  }`}
                >
                  {user?.name || "-"}
                </p>
                <p className="text-xs text-gray-500 leading-tight">{user?.designation || "—"}</p>
              </div>
            </div>
          );
        },
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
        renderCell: (params) => {
          const createdAt = params?.row?.createdAt;
          const fullDate = createdAt ? dayjs(createdAt).format("MMM D, YYYY [at] h:mm A") : "-";
          return (
            <Tooltip title={fullDate} placement="top" arrow>
              <p className="table-font-size text-gray-500 cursor-default">
                {createdAt ? dayjs(createdAt).fromNow() : "-"}
              </p>
            </Tooltip>
          );
        },
      },
    ],
    [navigate],
  );

  const handlePaginationChange = (model) => {
    setPaginationModel(model);
  };

  return (
    <BaseCard className="pt-4 pb-72 px-6 rounded-lg" elevation={1}>
      <BaseDataTableV2
        className="max-h-[calc(100vh-300px)]"
        rowHeight={52}
        columns={columns}
        data={workOrderLogs?.data?.logs}
        disableColumnResize={false}
        disableRowSelectionOnClick={true}
        isLoading={isLoading}
        hideFooter={false}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        rowCount={workOrderLogs?.data?.meta?.totalItems}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "bg-white" : "bg-gray-50"
        }
      />

      {selectedLogId && (
        <WorkOrderHistoryLog
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          logId={selectedLogId}
        />
      )}
    </BaseCard>
  );
};

export default HistoryTab;
